import React, { useState, useEffect, useMemo } from 'react';
import { FormField } from '../ui/FormField';
import { Button } from '../ui/Button';
import { Pagination } from '../ui/Pagination';
import { UI_THEME } from '../../config/constants';
import { findCompaniesService } from '../../services/findCompaniesService';
import { PubSubService } from '../../services/pubSubService';
import { useAuth } from '../../contexts/AuthContext';
import { Company } from '../../types/Company';
import { companyDedupeKey } from '../../utils/companyUtils';

const ITEMS_PER_PAGE = 10;

const criteriaTagStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '4px 10px',
  backgroundColor: '#eff6ff',
  color: '#1d4ed8',
  borderRadius: '20px',
  fontSize: '12px',
  fontWeight: 600,
  border: '1px solid #bfdbfe',
};

interface SearchGroup {
  searchId: string;
  sector?: string;
  industry?: string;
  geography?: string;
  companies: Company[];
}

function groupBySearch(companies: Company[]): SearchGroup[] {
  const map = new Map<string, SearchGroup>();
  companies.forEach(c => {
    const key = c.searchId ?? 'unknown';
    if (!map.has(key)) {
      map.set(key, {
        searchId: key,
        sector: c.sector,
        industry: c.industry,
        geography: c.geography,
        companies: [],
      });
    }
    map.get(key)!.companies.push(c);
  });
  return Array.from(map.values());
}

interface FindCompaniesViewProps {
  onCompaniesLoaded?: (companies: Company[]) => void;
  initialCompanies?: Company[];
  sectorFilter?: string[];
  geographyFilter?: string[];
  industryFilter?: string[];
  searchQuery?: string;
  leadsGeneratedCompanies?: Set<string>;
}

const FindCompaniesView: React.FC<FindCompaniesViewProps> = ({
  onCompaniesLoaded,
  initialCompanies,
  sectorFilter,
  geographyFilter,
  industryFilter,
  searchQuery,
  leadsGeneratedCompanies,
}) => {
  const { user } = useAuth();
  const [sector, setSector] = useState('');
  const [industry, setIndustry] = useState('');
  const [geography, setGeography] = useState('');
  const [warning, setWarning] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState<{ successful: number; failed: number } | null>(null);

  const toggleSelection = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  const handleGenerateLeads = async () => {
    if (!user || selectedKeys.size === 0) return;

    const selectedCompanies = companies.filter(c => {
      const key = `${c.searchId}-${c.companyName}`;
      return selectedKeys.has(key);
    });

    const companiesData = selectedCompanies
      .filter(c => c.geography)
      .map(c => ({ CompanyName: c.companyName, CompanyGeography: c.geography! }));

    if (companiesData.length === 0) {
      setError('Selected companies have no geography data. Please re-search with a geography value.');
      return;
    }

    setIsGenerating(true);
    setGenerateResult(null);
    try {
      const result = await PubSubService.publishCompanies(companiesData, user.uid);
      setGenerateResult({ successful: result.successful.length, failed: result.failed.length });
      clearSelection();
    } catch (err: any) {
      setError(err.message || 'Failed to trigger lead generation.');
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (!user) return;

    // Use pre-loaded data from Dashboard if available, skip Firestore read
    if (initialCompanies && initialCompanies.length > 0) {
      setCompanies(initialCompanies);
      setHasSearched(true);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    findCompaniesService.loadCompanies(user.uid)
      .then(loaded => {
        if (loaded.length > 0) {
          setCompanies(loaded);
          setHasSearched(true);
          onCompaniesLoaded?.(loaded);
        }
      })
      .catch(err => console.error('Failed to load companies from Firestore:', err))
      .finally(() => setIsLoading(false));
  }, [user, initialCompanies, onCompaniesLoaded]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!geography.trim()) {
      setWarning('Geography is required.');
      return;
    }
    if (!sector.trim() && !industry.trim()) {
      setWarning('Please provide a Sector or Industry in addition to Geography.');
      return;
    }

    if (!user) {
      setError('You must be signed in to search for companies.');
      return;
    }

    setWarning('');
    setError('');
    setIsSubmitting(true);

    try {
      const request = {
        sector: sector.trim() || undefined,
        industry: industry.trim() || undefined,
        geography: geography.trim() || undefined,
      };
      const response = await findCompaniesService.findCompanies(request, user.uid);
      const enriched: Company[] = response.companies.map(c => ({
        ...c,
        searchId: response.searchId,
        sector: request.sector,
        industry: request.industry,
        geography: request.geography,
      }));
      setCompanies(prev => {
        // Drop any prior results from this search id, then merge while
        // de-duplicating by the company's normalized website (falling back to
        // name) so a company already shown from an earlier search is not added
        // again. Two companies can share a name but not a primary domain.
        const previous = prev.filter(p => p.searchId !== response.searchId);
        const seen = new Set<string>();
        const combined = [...enriched, ...previous].filter(c => {
          const key = companyDedupeKey(c);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        onCompaniesLoaded?.(combined);
        return combined;
      });
      setHasSearched(true);
    } catch (err: any) {
      setError(err.message || 'Failed to find companies. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    if (warning) setWarning('');
    if (error) setError('');
  };

  const visibleCompanies = useMemo(() => {
    let result = companies;
    if (sectorFilter && sectorFilter.length > 0) {
      result = result.filter(c => c.sector != null && sectorFilter.includes(c.sector));
    }
    if (geographyFilter && geographyFilter.length > 0) {
      result = result.filter(c => c.geography != null && geographyFilter.includes(c.geography));
    }
    if (industryFilter && industryFilter.length > 0) {
      result = result.filter(c => c.industry != null && industryFilter.includes(c.industry));
    }
    if (searchQuery?.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.companyName.toLowerCase().includes(q) ||
        c.rationale.toLowerCase().includes(q)
      );
    }
    return result;
  }, [companies, sectorFilter, geographyFilter, industryFilter, searchQuery]);

  // Reset to page 1 whenever filters or search change
  const visibleKey = `${(sectorFilter ?? []).join(',')}|${(geographyFilter ?? []).join(',')}|${(industryFilter ?? []).join(',')}|${searchQuery}`;
  useEffect(() => { setCurrentPage(1); }, [visibleKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(visibleCompanies.length / ITEMS_PER_PAGE);
  const pagedCompanies = visibleCompanies.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );
  const searchGroups = groupBySearch(pagedCompanies);

  return (
    <div style={{
      position: 'relative',
      width: '100vw',
      marginLeft: 'calc(-50vw + 50%)',
      padding: `${UI_THEME.spacing.md} 0`,
      backgroundColor: UI_THEME.colors.background.secondary,
      minHeight: 'calc(100vh - 200px)',
    }}>
      <div className="container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: UI_THEME.spacing.xl,
        paddingLeft: UI_THEME.spacing.lg,
        paddingRight: UI_THEME.spacing.lg,
      }}>

        {/* Left Column - Form */}
        <div className="find-companies-form" style={{
          width: '400px',
          minWidth: '350px',
          maxWidth: '450px',
          background: 'linear-gradient(135deg, #fefbff 0%, #fbfcff 50%, #f8faff 100%)',
          borderRadius: '16px',
          padding: `${UI_THEME.spacing.lg} ${UI_THEME.spacing.xl}`,
          boxShadow: '0 20px 25px -5px rgba(59, 130, 246, 0.15), 0 10px 10px -5px rgba(59, 130, 246, 0.08)',
          border: '1px solid #93c5fd',
          animation: UI_THEME.animations.fadeIn,
          position: 'sticky',
          top: UI_THEME.spacing.md,
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
        }}>
          <div style={{ textAlign: 'center', marginBottom: UI_THEME.spacing.sm }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: UI_THEME.colors.gray[900],
              marginBottom: UI_THEME.spacing.xs,
              letterSpacing: '-0.025em',
            }}>
              Find Companies
            </h2>
            <p style={{
              color: UI_THEME.colors.gray[600],
              fontSize: UI_THEME.fontSize.sm,
              lineHeight: '1.5',
              margin: 0,
            }}>
              Geography is required. You must also provide a Sector or Industry.
            </p>
          </div>

          <form onSubmit={handleSubmit} style={{ animation: UI_THEME.animations.slideUp }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              <FormField
                id="find-geography"
                label="Geography"
                value={geography}
                onChange={handleFieldChange(setGeography)}
                placeholder="e.g., Sweden, Europe, New York"
                disabled={isSubmitting}
                borderColor="#c4b5fd"
                focusBorderColor="#a78bfa"
              />
              <FormField
                id="find-sector"
                label="Sector"
                value={sector}
                onChange={handleFieldChange(setSector)}
                placeholder="e.g., Technology, Healthcare"
                disabled={isSubmitting}
                borderColor="#c4b5fd"
                focusBorderColor="#a78bfa"
              />
              <FormField
                id="find-industry"
                label="Industry"
                value={industry}
                onChange={handleFieldChange(setIndustry)}
                placeholder="e.g., SaaS, Pharmaceuticals, Retail"
                disabled={isSubmitting}
                borderColor="#c4b5fd"
                focusBorderColor="#a78bfa"
              />
            </div>

            {warning && (
              <div style={{
                marginBottom: UI_THEME.spacing.md,
                padding: UI_THEME.spacing.md,
                backgroundColor: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '8px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: UI_THEME.spacing.sm,
                  color: '#92400e',
                  fontSize: UI_THEME.fontSize.sm,
                  fontWeight: UI_THEME.fontWeight.medium,
                }}>
                  <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {warning}
                </div>
              </div>
            )}

            {error && (
              <div style={{
                marginBottom: UI_THEME.spacing.md,
                padding: UI_THEME.spacing.md,
                backgroundColor: UI_THEME.colors.background.error,
                border: `1px solid ${UI_THEME.colors.background.errorBorder}`,
                borderRadius: '8px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: UI_THEME.spacing.sm,
                  color: '#dc2626',
                  fontSize: UI_THEME.fontSize.sm,
                  fontWeight: UI_THEME.fontWeight.medium,
                }}>
                  <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
              size="lg"
              style={{
                width: '100%',
                backgroundColor: isSubmitting ? '#6b7280' : '#1d4ed8',
                borderColor: isSubmitting ? '#6b7280' : '#1d4ed8',
                fontSize: '16px',
                fontWeight: '600',
                height: '48px',
                borderRadius: '12px',
                boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(29, 78, 216, 0.25)',
                transition: 'all 0.2s ease',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting ? 'Searching...' : 'Find Companies'}
            </Button>
          </form>
        </div>

        {/* Right Column - Results */}
        <div style={{
          flex: 1,
          minHeight: '400px',
          paddingLeft: UI_THEME.spacing.md,
        }}>
          {isLoading && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              gap: UI_THEME.spacing.md,
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '3px solid #e2e8f0',
                borderTop: '3px solid #1d4ed8',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
              <p style={{ color: UI_THEME.colors.gray[600], fontSize: UI_THEME.fontSize.sm }}>
                Loading companies...
              </p>
            </div>
          )}

          {!isLoading && isSubmitting && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              gap: UI_THEME.spacing.md,
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '3px solid #e2e8f0',
                borderTop: '3px solid #1d4ed8',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
              <p style={{ color: UI_THEME.colors.gray[600], fontSize: UI_THEME.fontSize.sm }}>
                Searching for companies...
              </p>
            </div>
          )}

          {!isLoading && !isSubmitting && !hasSearched && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '48px',
              color: '#6b7280',
            }}>
              <div style={{
                textAlign: 'center',
                padding: '32px',
                backgroundColor: '#f9fafb',
                borderRadius: '12px',
                border: '2px dashed #d1d5db',
                maxWidth: '400px',
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏢</div>
                <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                  Find Companies
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  Enter a Geography and at least one of Sector or Industry to discover matching companies.
                </div>
              </div>
            </div>
          )}

          {!isLoading && !isSubmitting && hasSearched && companies.length === 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '48px',
              color: '#6b7280',
            }}>
              <div style={{
                textAlign: 'center',
                padding: '32px',
                backgroundColor: '#f9fafb',
                borderRadius: '12px',
                border: '2px dashed #d1d5db',
                maxWidth: '400px',
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                  No Companies Found
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  No matching companies were found for your search. Try adjusting your sector, industry, or geography.
                </div>
              </div>
            </div>
          )}

          {!isLoading && !isSubmitting && companies.length > 0 && (
            <div style={{ animation: UI_THEME.animations.fadeIn }}>
              <div style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: UI_THEME.spacing.sm,
                marginBottom: UI_THEME.spacing.lg,
              }}>
                <h2 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: UI_THEME.colors.gray[900],
                  margin: 0,
                }}>
                  Companies Found
                </h2>
                <span style={{
                  fontSize: UI_THEME.fontSize.sm,
                  color: UI_THEME.colors.gray[500],
                }}>
                  {visibleCompanies.length}{visibleCompanies.length !== companies.length ? ` of ${companies.length}` : ''} results
                </span>
              </div>

              {generateResult && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: UI_THEME.spacing.sm,
                  backgroundColor: generateResult.failed === 0 ? '#f0fdf4' : '#fffbeb',
                  border: `1px solid ${generateResult.failed === 0 ? '#86efac' : '#fde68a'}`,
                  borderRadius: '10px',
                  padding: `${UI_THEME.spacing.sm} ${UI_THEME.spacing.md}`,
                  marginBottom: UI_THEME.spacing.md,
                  fontSize: UI_THEME.fontSize.sm,
                  color: generateResult.failed === 0 ? '#166534' : '#92400e',
                  fontWeight: 500,
                }}>
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
                    {generateResult.failed === 0
                      ? <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      : <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    }
                  </svg>
                  {generateResult.failed === 0
                    ? `Lead generation started for ${generateResult.successful} ${generateResult.successful === 1 ? 'company' : 'companies'}. Check the All Leads tab shortly.`
                    : `${generateResult.successful} queued, ${generateResult.failed} failed. Some companies could not be queued.`
                  }
                  <button onClick={() => setGenerateResult(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.6, fontSize: '16px', lineHeight: 1 }}>×</button>
                </div>
              )}

              {selectedKeys.size > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '10px',
                  padding: `${UI_THEME.spacing.sm} ${UI_THEME.spacing.md}`,
                  marginBottom: UI_THEME.spacing.md,
                  gap: UI_THEME.spacing.md,
                }}>
                  <span style={{ fontSize: UI_THEME.fontSize.sm, fontWeight: 600, color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                    {selectedKeys.size} {selectedKeys.size === 1 ? 'company' : 'companies'} selected
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: UI_THEME.spacing.sm }}>
                    <button
                      onClick={handleGenerateLeads}
                      disabled={isGenerating}
                      style={{
                        backgroundColor: isGenerating ? '#6b7280' : '#1d4ed8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontSize: UI_THEME.fontSize.sm,
                        fontWeight: 600,
                        cursor: isGenerating ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: isGenerating ? 'none' : '0 2px 8px rgba(29,78,216,0.3)',
                        transition: 'all 0.15s ease',
                        whiteSpace: 'nowrap',
                        opacity: isGenerating ? 0.7 : 1,
                      }}
                      onMouseEnter={e => {
                        if (!isGenerating) {
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1e40af';
                          (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(29,78,216,0.4)';
                        }
                      }}
                      onMouseLeave={e => {
                        if (!isGenerating) {
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1d4ed8';
                          (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 8px rgba(29,78,216,0.3)';
                        }
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                        <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                      </svg>
                      {isGenerating ? 'Generating...' : 'Generate Leads'}
                    </button>
                    <button
                      onClick={clearSelection}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#6b7280',
                        fontSize: UI_THEME.fontSize.sm,
                        cursor: 'pointer',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: UI_THEME.spacing.xl, marginBottom: UI_THEME.spacing.lg }}>
                {searchGroups.map(group => (
                  <div key={group.searchId}>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: UI_THEME.spacing.xs,
                      marginBottom: UI_THEME.spacing.md,
                    }}>
                      {group.sector && (
                        <span style={criteriaTagStyle}>Sector: {group.sector}</span>
                      )}
                      {group.geography && (
                        <span style={criteriaTagStyle}>Geography: {group.geography}</span>
                      )}
                      {group.industry && (
                        <span style={criteriaTagStyle}>Industry: {group.industry}</span>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: UI_THEME.spacing.md }}>
                      {group.companies.map((company) => {
                        const key = `${group.searchId}-${company.companyName}`;
                        const isSelected = selectedKeys.has(key);
                        const hasLeads = leadsGeneratedCompanies?.has(company.companyName.toLowerCase()) ?? false;
                        return (
                          <div
                            key={key}
                            onClick={() => !hasLeads && toggleSelection(key)}
                            style={{
                              backgroundColor: isSelected ? '#eff6ff' : hasLeads ? '#fafafa' : 'white',
                              borderRadius: '12px',
                              padding: UI_THEME.spacing.lg,
                              boxShadow: isSelected
                                ? '0 0 0 2px #1d4ed8, 0 4px 12px rgba(29,78,216,0.15)'
                                : UI_THEME.shadows.card,
                              border: isSelected ? '1px solid #1d4ed8' : hasLeads ? '1px solid #d1fae5' : '1px solid #e5e7eb',
                              transition: 'all 0.15s ease',
                              cursor: hasLeads ? 'default' : 'pointer',
                              userSelect: 'none',
                              position: 'relative',
                              opacity: hasLeads ? 0.75 : 1,
                            }}
                            onMouseEnter={e => {
                              if (!isSelected && !hasLeads) (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 16px -4px rgba(0,0,0,0.12)';
                            }}
                            onMouseLeave={e => {
                              if (!isSelected && !hasLeads) (e.currentTarget as HTMLDivElement).style.boxShadow = UI_THEME.shadows.card;
                            }}
                          >
                            {isSelected && (
                              <div style={{
                                position: 'absolute',
                                top: '12px',
                                right: '12px',
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#1d4ed8',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}>
                                <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                                  <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: UI_THEME.spacing.sm, marginBottom: UI_THEME.spacing.sm, paddingRight: isSelected ? '28px' : '0' }}>
                              <h3 style={{
                                fontSize: UI_THEME.fontSize.base,
                                fontWeight: '600',
                                color: isSelected ? '#1d4ed8' : UI_THEME.colors.gray[900],
                                margin: 0,
                              }}>
                                {company.companyName}
                              </h3>
                              {hasLeads && (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '2px 8px',
                                  backgroundColor: '#f0fdf4',
                                  color: '#166534',
                                  border: '1px solid #86efac',
                                  borderRadius: '20px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap',
                                }}>
                                  <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Leads generated
                                </span>
                              )}
                            </div>
                            <p style={{
                              fontSize: UI_THEME.fontSize.sm,
                              color: UI_THEME.colors.gray[600],
                              lineHeight: '1.6',
                              margin: 0,
                            }}>
                              {company.rationale}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={visibleCompanies.length}
                itemsPerPage={ITEMS_PER_PAGE}
                itemLabel="companies"
              />

            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .find-companies-form::-webkit-scrollbar { width: 8px; }
        .find-companies-form::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 4px; }
        .find-companies-form::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .find-companies-form::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .find-companies-form { scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9; }
      `}</style>
    </div>
  );
};

export default FindCompaniesView;
